/**
 * Minimal SPA meta updates for public pages (safe, idempotent upserts).
 */

const META_ATTR = "data-td-page-meta";
const KEY_ATTR = "data-td-meta-key";
const JSON_LD_ID = "td-page-jsonld";

/**
 * @typedef {Object} PageMetaOptions
 * @property {string} [title]
 * @property {string} [description]
 * @property {string} [ogTitle]
 * @property {string} [ogDescription]
 * @property {string} [ogImage]
 * @property {string} [ogType]
 * @property {string} [ogUrl]
 * @property {string} [canonicalUrl]
 * @property {string} [robots] e.g. "noindex, nofollow"
 * @property {string} [twitterCard] summary | summary_large_image
 * @property {string} [twitterTitle]
 * @property {string} [twitterDescription]
 * @property {string} [twitterImage]
 * @property {object|object[]} [jsonLd]
 */

function upsertMetaByKey(key, createElement) {
  const sel = `meta[${KEY_ATTR}="${key}"][${META_ATTR}]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = createElement();
    el.setAttribute(KEY_ATTR, key);
    el.setAttribute(META_ATTR, "1");
    document.head.appendChild(el);
  }
  return el;
}

function removeManagedMetas() {
  document.head.querySelectorAll(`meta[${META_ATTR}]`).forEach((n) => n.remove());
  const link = document.head.querySelector(`link[rel="canonical"][${META_ATTR}]`);
  if (link) link.remove();
  const jsonLd = document.getElementById(JSON_LD_ID);
  if (jsonLd) jsonLd.remove();
}

function upsertJsonLd(data) {
  let el = document.getElementById(JSON_LD_ID);
  if (!data) {
    if (el) el.remove();
    return;
  }
  const payload = Array.isArray(data) ? data : [data];
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = JSON_LD_ID;
    el.setAttribute(META_ATTR, "1");
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload.length === 1 ? payload[0] : payload);
}

/**
 * @param {PageMetaOptions} opts
 */
export function setPageMeta(opts = {}) {
  const {
    title,
    description,
    ogTitle,
    ogDescription,
    ogImage,
    ogType = "website",
    ogUrl,
    canonicalUrl,
    robots,
    twitterCard = "summary_large_image",
    twitterTitle,
    twitterDescription,
    twitterImage,
    jsonLd,
  } = opts;

  if (title) document.title = title;

  if (description != null && String(description).trim()) {
    const el = upsertMetaByKey("description", () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description");
      return m;
    });
    el.setAttribute("content", String(description).trim());
  }

  if (robots != null && String(robots).trim()) {
    const el = upsertMetaByKey("robots", () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "robots");
      return m;
    });
    el.setAttribute("content", String(robots).trim());
  }

  const ot = (ogTitle || title || "").trim();
  if (ot) {
    const el = upsertMetaByKey("og:title", () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:title");
      return m;
    });
    el.setAttribute("content", ot);
  }

  const od = (ogDescription || description || "").trim();
  if (od) {
    const el = upsertMetaByKey("og:description", () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:description");
      return m;
    });
    el.setAttribute("content", od);
  }

  const oImg = (ogImage || twitterImage || "").trim();
  if (oImg) {
    const el = upsertMetaByKey("og:image", () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:image");
      return m;
    });
    el.setAttribute("content", oImg);
  }

  if (ogUrl != null && String(ogUrl).trim()) {
    const el = upsertMetaByKey("og:url", () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:url");
      return m;
    });
    el.setAttribute("content", String(ogUrl).trim());
  }

  {
    const el = upsertMetaByKey("og:type", () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:type");
      return m;
    });
    el.setAttribute("content", String(ogType || "website").trim());
  }

  {
    const el = upsertMetaByKey("og:site_name", () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:site_name");
      return m;
    });
    el.setAttribute("content", "Treydora");
  }

  const twCard = String(twitterCard || "summary_large_image").trim();
  {
    const el = upsertMetaByKey("twitter:card", () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "twitter:card");
      return m;
    });
    el.setAttribute("content", twCard);
  }

  const twt = (twitterTitle || ogTitle || title || "").trim();
  if (twt) {
    const el = upsertMetaByKey("twitter:title", () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "twitter:title");
      return m;
    });
    el.setAttribute("content", twt);
  }

  const twd = (twitterDescription || ogDescription || description || "").trim();
  if (twd) {
    const el = upsertMetaByKey("twitter:description", () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "twitter:description");
      return m;
    });
    el.setAttribute("content", twd);
  }

  const twImg = (twitterImage || ogImage || "").trim();
  if (twImg) {
    const el = upsertMetaByKey("twitter:image", () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "twitter:image");
      return m;
    });
    el.setAttribute("content", twImg);
  }

  let canon = document.head.querySelector(`link[rel="canonical"][${META_ATTR}]`);
  if (canonicalUrl && String(canonicalUrl).trim()) {
    const href = String(canonicalUrl).trim();
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      canon.setAttribute(META_ATTR, "1");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", href);
  } else if (canon) {
    canon.remove();
  }

  if (jsonLd) {
    upsertJsonLd(jsonLd);
  } else {
    upsertJsonLd(null);
  }
}

/** Remove tags we added (optional cleanup on unmount). */
export function clearPageMeta() {
  removeManagedMetas();
}
